import React from 'react';
import { createPortal } from 'react-dom';
import { UI_Z_INDEX } from '../utils/uiZIndex';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 350;

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Wrapper um Input + Dropdown */
  className?: string;
  /** Klassen nur für das Input-Element */
  inputClassName?: string;
  disabled?: boolean;
  id?: string;
  autoComplete?: string;
};

/**
 * Adressfeld mit Vorschlägen aus OpenStreetMap (Nominatim) über GET /api/address-suggest.
 */
export function AddressAutocompleteInput({
  value,
  onChange,
  placeholder,
  className = '',
  inputClassName = '',
  disabled,
  id,
  autoComplete = 'off',
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<string[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Only search after keyboard edits — not on mount/prefill or after picking a suggestion. */
  const userTypedRef = React.useRef(false);

  const updateMenuPosition = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  React.useLayoutEffect(() => {
    if (!open || suggestions.length === 0) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, suggestions.length, updateMenuPosition]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  React.useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = value.trim();
    if (q.length < MIN_CHARS) {
      setSuggestions([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    if (!userTypedRef.current) {
      setSuggestions([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      fetch(`/api/address-suggest?q=${encodeURIComponent(q)}`, {
        signal: ac.signal,
        credentials: 'include',
      })
        .then((r) => (r.ok ? r.json() : { suggestions: [] }))
        .then((data: { suggestions?: string[] }) => {
          if (!userTypedRef.current) return;
          const list = Array.isArray(data.suggestions) ? data.suggestions : [];
          setSuggestions(list);
          setHighlight(0);
          setOpen(list.length > 0);
        })
        .catch(() => {
          if (ac.signal.aborted) return;
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  const pick = (s: string) => {
    userTypedRef.current = false;
    onChange(s);
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
  };

  const listboxId = `${id ?? 'addr'}-listbox`;
  const dropdown =
    open && suggestions.length > 0 && menuPos
      ? createPortal(
          <div
            ref={listRef}
            data-address-autocomplete-list
            className="fixed max-h-52 overflow-auto rounded-md border border-app-border bg-app-surface py-1 text-left shadow-lg"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              zIndex: UI_Z_INDEX.portalDropdown,
            }}
          >
            <ul id={listboxId} role="listbox" className="m-0 list-none p-0">
              {suggestions.map((s, i) => (
                <li key={`${i}-${s.slice(0, 32)}`} role="option" aria-selected={i === highlight}>
                  <button
                    type="button"
                    className={`w-full px-3 py-2 text-left text-xs text-app-text hover:bg-app-hover ${
                      i === highlight ? 'bg-sky-50' : ''
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setHighlight(i)}
                    onClick={() => pick(s)}
                  >
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        id={id}
        type="text"
        disabled={disabled}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => {
          userTypedRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={open ? listboxId : undefined}
      />
      {loading && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">…</span>
      )}
      {dropdown}
      {(loading || (open && suggestions.length > 0)) && (
        <p className="mt-1 text-[10px] text-slate-400 leading-snug">
          Vorschläge: ©{' '}
          <a
            href="https://www.openstreetmap.org/copyright"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-app-text-muted"
          >
            OpenStreetMap
          </a>
        </p>
      )}
    </div>
  );
}
